import "./styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider, type NavigateOptions, type ToOptions } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nProvider, RouterProvider as AriaRouterProvider } from "react-aria-components";
import { setFormatPrefs } from "@offsilo/ui/format";
import { UIStringsProvider } from "@offsilo/ui/strings";
import { Toaster } from "@offsilo/ui/toast";
import { bootRuntime } from "./app/boot";
import { ChromeProvider } from "./app/chrome";
import { installChunkReload, RouteError } from "./app/chunk-reload";
import { uiStrings } from "./app/strings";
import { getLocale } from "./paraglide/runtime.js";
import { routeTree } from "./routeTree.gen";

// data stays fresh for 30 s and is kept 10 min after its last use; realtime (data/realtime.ts) and the screens that need
// live values (timers, presence, refetchInterval) set their own freshness, so the defaults only save repeat round trips
const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000, gcTime: 10 * 60_000 } } });
// a lazy chunk gone after a deploy reloads the page once (chunk-reload.tsx)
installChunkReload();
// links prefetch their route's code on hover or focus; the data is governed by the query cache (preloadStaleTime 0)
const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", defaultPreloadStaleTime: 0, scrollRestoration: false, defaultErrorComponent: RouteError });

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}
declare module "react-aria-components" {
  interface RouterConfig {
    href: ToOptions["to"] | string;
    routerOptions: Omit<NavigateOptions, keyof ToOptions>;
  }
}

const locale = getLocale();
setFormatPrefs({ locale: navigator.language || locale });
document.documentElement.lang = locale;
// service worker, offline queue, and the workspace's saved formats (replace the browser's defaults once they arrive)
bootRuntime(queryClient, () => void router.invalidate());

function Root() {
  return (
    <AriaRouterProvider
      navigate={(href, opts) => router.navigate({ ...(opts ?? {}), to: href as string })}
      useHref={(href) => router.buildLocation({ to: href as string }).href}
    >
      <I18nProvider locale={navigator.language || locale}>
        <UIStringsProvider value={uiStrings()}>
          <QueryClientProvider client={queryClient}>
            <ChromeProvider>
              <RouterProvider router={router} />
            </ChromeProvider>
          </QueryClientProvider>
          <Toaster />
        </UIStringsProvider>
      </I18nProvider>
    </AriaRouterProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
